import React, { useState } from "react";
import { Modal, Steps, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import CustomTabs from "../Tabs/CustomTab";

const { Step } = Steps;

// Custom Steps Component with configurable styles
const CustomSteps = ({
  steps,
  current,
  onChange,
  className = "",
  stepStyles = {},
}) => {
  // Default step styles
  const defaultStyles = {
    activeColor: "#F9A022",
    finishedColor: "#F9A022",
    waitingColor: "#E5E7EB",
    activeTextColor: "#F9A022",
    finishedTextColor: "#F9A022",
    waitingTextColor: "#9CA3AF",
    connectorColor: "#E5E7EB",
    waitingConnectorColor: "#E5E7EB",
  };

  // Merge default styles with custom styles
  const styles = { ...defaultStyles, ...stepStyles };

  return (
    <div className={`custom-steps-wrapper ${className}`}>
      <style>{`
        .custom-steps-wrapper .ant-steps-item-finish .ant-steps-item-icon {
          background-color: ${styles.finishedColor} !important;
          border-color: ${styles.finishedColor} !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
          color: white !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-process .ant-steps-item-icon {
          background-color: ${styles.activeColor} !important;
          border-color: ${styles.activeColor} !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-process .ant-steps-item-icon > .ant-steps-icon {
          color: white !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-wait .ant-steps-item-icon {
          background-color: ${styles.waitingColor} !important;
          border-color: ${styles.waitingColor} !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
          color: ${styles.waitingTextColor} !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-finish .ant-steps-item-title {
          color: ${styles.finishedTextColor} !important;
          font-weight: 400 !important;
          font-size:0.8rem;
        }
        
        .custom-steps-wrapper .ant-steps-item-process .ant-steps-item-title {
          color: ${styles.activeTextColor} !important;
          font-weight: 400 !important;
          font-size:0.8rem;
        }
        
        .custom-steps-wrapper .ant-steps-item-wait .ant-steps-item-title {
          color: ${styles.waitingTextColor} !important;
          font-size:0.8rem;
        }
        
        .custom-steps-wrapper .ant-steps-item-finish .ant-steps-item-tail::after {
          background-color: ${styles.connectorColor} !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-process .ant-steps-item-tail::after {
          background-color: ${styles.waitingConnectorColor} !important;
        }
        
        .custom-steps-wrapper .ant-steps-item-wait .ant-steps-item-tail::after {
          background-color: ${styles.waitingConnectorColor} !important;
        }
      `}</style>

      <Steps current={current} onChange={onChange} size="small">
        {steps.map((step, index) => (
          <Step
            key={index}
            title={step.title}
            description={step.description}
            disabled={step.disabled}
          />
        ))}
      </Steps>
    </div>
  );
};

const ReusableModal = ({
  // Modal props
  open,
  onClose,
  title,
  subtitle,
  width,
  height,
  // Tabs configuration
  showTabs = true,
  tabs = [],
  activeTab = null,
  onTabChange = null,
  // Steps configuration
  steps = [],
  showSteps = true,
  currentStep = 0,
  onStepChange,
  stepStyles = {},
  stepsClassName = "mb-4 w-[80%] mx-auto",

  // Content and actions
  children,
  footer,
  customFooter = false,

  // Navigation buttons
  showNavigation = true,
  nextText = "Next",
  prevText = "Previous",
  finishText = "Finish",
  onNext,
  onPrevious,
  onFinish,
  nextDisabled = false,

  // Position and animation props
  position = "center", // "center", "right", "left", "top", "bottom"
  slideDirection = "none", // "none", "right", "left", "top", "bottom"
  animationDuration = 300,

  // Form integration
  form,
  onFormFinish,

  // Custom styling
  className = "",
  bodyStyle = {},

  // Loading states
  isLoading,
  loading = false,
  nextLoading = false,
}) => {
  const [internalStep, setInternalStep] = useState(0);
  const activeStep = currentStep !== undefined ? currentStep : internalStep;
  const isLastStep = activeStep === steps.length - 1;
  const isFirstStep = activeStep === 0;
  // Render tabs content
  const renderTabsContent = () => {
    if (!tabs) return null;

    return (
      <CustomTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        className="mb-0 bg-[#F9FAFB] rounded-md"
      />
    );
  };
  // Get animation transition names based on slide direction. When there's
  // no slide direction, leave transitionName undefined so antd's own
  // built-in (working) modal animation is used - getAnimationCSS() below
  // only defines CSS for the slide-* variants, so overriding it with a
  // "zoom" name here had no matching CSS and left rc-motion waiting
  // forever for a transitionend event that never fires, permanently
  // stuck mid-close (Modal never visually disappears even once open
  // becomes false).
  const getTransitionNames = () => {
    return {
      transitionName:
        slideDirection !== "none" ? `slide-${slideDirection}` : undefined,
      maskTransitionName: "fade",
    };
  };

  // Get positioning styles based on position prop
  const getPositionStyles = () => {
    const baseStyles = {
      margin: "auto 1rem",
    };

    switch (position) {
      case "right":
        return {
          ...baseStyles,
          position: "fixed",
          right: 0,
          top: 0,
          height: height || "90vh",
          maxWidth: "none",
          borderRadius: "0",
        };
      case "left":
        return {
          ...baseStyles,
          position: "fixed",
          left: 0,
          top: 0,
          height: height || "70vh",
          maxWidth: "none",
          borderRadius: "0",
        };
      case "top":
        return {
          ...baseStyles,
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: width,
        };
      case "bottom":
        return {
          ...baseStyles,
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: width,
        };
      default: // center
        return {};
    }
  };

  // Generate CSS for custom animations using Ant Design's animation system
  const getAnimationCSS = () => {
    return `
      /* Right slide animations */
      .slide-right-enter {
        transform: translateX(-100%);
        opacity: 0;
      }
      
      .slide-right-enter-active {
        transform: translateX(0);
        opacity: 1;
        transition: all ${animationDuration}ms ease-out;
      }
      
      .slide-right-leave {
        transform: translateX(0);
        opacity: 1;
      }
      
      .slide-right-leave-active {
        transform: translateX(100%);
        opacity: 0;
        transition: all ${animationDuration}ms ease-in;
      }

      /* Left slide animations */
      .slide-left-enter {
        transform: translateX(-100%);
        opacity: 0;
      }
      
      .slide-left-enter-active {
        transform: translateX(0);
        opacity: 1;
        transition: all ${animationDuration}ms ease-out;
      }
      
      .slide-left-leave {
        transform: translateX(0);
        opacity: 1;
      }
      
      .slide-left-leave-active {
        transform: translateX(-100%);
        opacity: 0;
        transition: all ${animationDuration}ms ease-in;
      }

      /* Top slide animations */
      .slide-top-enter {
        transform: translateY(-100%);
        opacity: 0;
      }
      
      .slide-top-enter-active {
        transform: translateY(0);
        opacity: 1;
        transition: all ${animationDuration}ms ease-out;
      }
      
      .slide-top-leave {
        transform: translateY(0);
        opacity: 1;
      }
      
      .slide-top-leave-active {
        transform: translateY(-100%);
        opacity: 0;
        transition: all ${animationDuration}ms ease-in;
      }

      /* Bottom slide animations */
      .slide-bottom-enter {
        transform: translateY(100%);
        opacity: 0;
      }
      
      .slide-bottom-enter-active {
        transform: translateY(0);
        opacity: 1;
        transition: all ${animationDuration}ms ease-out;
      }
      
      .slide-bottom-leave {
        transform: translateY(0);
        opacity: 1;
      }
      
      .slide-bottom-leave-active {
        transform: translateY(100%);
        opacity: 0;
        transition: all ${animationDuration}ms ease-in;
      }

      /* Fade mask animation */
      .fade-enter {
        opacity: 0;
      }
      
      .fade-enter-active {
        opacity: 0.45;
        transition: opacity ${animationDuration}ms ease;
      }
      
      .fade-leave {
        opacity: 0.45;
      }
      
      .fade-leave-active {
        opacity: 0;
        transition: opacity ${animationDuration}ms ease;
      }

      /* Position specific styles */
      .modal-right .ant-modal {
        max-width: none;
        padding: 0;
      }

      .modal-right .ant-modal-content {
        height: 96vh;
        top:1rem;
        right:0rem;

        border-radius: 15px;
        // overflow-y: auto;
      }

      .modal-right .ant-modal-body {
        height: 100%;
        overflow-y: auto;
        padding: 10px;
        /* Hide scrollbar for Chrome, Safari and Opera */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
        }

      .modal-right .ant-modal-body::-webkit-scrollbar {
        display: none; /* Chrome, Safari and Opera */
      }
      

      .modal-left .ant-modal {
        max-width: none;
        padding: 0;
      }

      .modal-left .ant-modal-content {
        height: 100vh;
        border-radius: 0;
        overflow-y: auto;
      }

      .modal-left .ant-modal-body {
        height: calc(100vh - 0px);
        overflow-y: auto;
        padding: 24px;
      }
    `;
  };

  const handleStepChange = (step) => {
    if (onStepChange) {
      onStepChange(step);
    } else {
      setInternalStep(step);
    }
  };

  const handleNext = async () => {
    if (form) {
      try {
        await form.validateFields();
        if (onNext) {
          await onNext(activeStep);
        } else {
          handleStepChange(activeStep + 1);
        }
      } catch (error) {
        console.log("Validation failed:", error);
      }
    } else {
      if (onNext) {
        await onNext(activeStep);
      } else {
        handleStepChange(activeStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious(activeStep);
    } else {
      handleStepChange(activeStep - 1);
    }
  };

  const handleFinish = async () => {
    if (form && onFormFinish) {
      try {
        const values = await form.validateFields();
        await onFormFinish(values);
      } catch (error) {
        console.log("Validation failed:", error);
      }
    } else if (onFinish) {
      await onFinish();
    }
  };

  const renderDefaultFooter = () => {
    if (!showNavigation) return null;

    return (
      <div className="flex justify-between items-center">
        <div>
          {!isFirstStep && (
            <Button onClick={handlePrevious} disabled={loading}>
              {prevText}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          {isLastStep ? (
            <Button
              type="primary"
              onClick={handleFinish}
              loading={isLoading}
              className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:from-[#F9A022] hover:to-[#D06D01] border-0"
            >
              {finishText}
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleNext}
              disabled={nextDisabled}
              loading={nextLoading || loading}
              className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:from-[#F9A022] hover:to-[#D06D01] border-0"
            >
              {nextText}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const { transitionName, maskTransitionName } = getTransitionNames();

  return (
    <>
      <style>{getAnimationCSS()}</style>
      <Modal
        open={open}
        onCancel={onClose}
        title={null}
        footer={null}
        width={width}
        className={`custom-modal ${position === "right" ? "modal-right" : ""} ${
          position === "left" ? "modal-left" : ""
        } ${className}`}
        style={getPositionStyles()}
        styles={{ body: bodyStyle }}
        closeIcon={<CloseOutlined />}
        centered={position === "center"}
        maskClosable={true}
        destroyOnClose={true}
        transitionName={transitionName}
        maskTransitionName={maskTransitionName}
      >
        {/* Modal Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                {title}
              </h2>
              {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
            </div>
          </div>

          {/* Custom Steps */}
          {showSteps && steps.length > 0 && (
            <CustomSteps
              steps={steps}
              current={activeStep}
              onChange={handleStepChange}
              className={stepsClassName}
              stepStyles={stepStyles}
            />
          )}
          {showTabs && <div className="flex">{renderTabsContent()}</div>}
        </div>

        {/* Modal Content */}
        <div className="modal-content min-h-[300px]">{children}</div>

        {/* Modal Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          {customFooter ? footer : renderDefaultFooter()}
        </div>
      </Modal>
    </>
  );
};
export { ReusableModal, CustomSteps };
